import React, { useState, useMemo } from 'react';
import { Button, Modal } from 'semantic-ui-react';
import { Copy, ClipboardPaste, ChevronDown, ChevronUp, Plus, Minus, RefreshCw, Check, AlertTriangle } from 'lucide-react';
import _ from 'lodash';
function computeFieldDiff(currentFields, incomingFields, currentStyle, incomingStyle, currentActions, incomingActions) {
    const currentByKey = new Map(currentFields.map(f => [f.key, f]));
    const incomingByKey = new Map(incomingFields.map(f => [f.key, f]));
    const added = incomingFields.filter(f => !currentByKey.has(f.key));
    const removed = currentFields.filter(f => !incomingByKey.has(f.key));
    const modified = incomingFields
        .filter(f => {
        const curr = currentByKey.get(f.key);
        return curr && (curr.type !== f.type || curr.label !== f.label);
    })
        .map(f => ({ current: currentByKey.get(f.key), incoming: f }));
    return {
        added,
        removed,
        modified,
        styleChanged: !_.isEqual(currentStyle, incomingStyle),
        actionsChanged: !_.isEqual(currentActions, incomingActions),
    };
}
function parseTransferPayload(raw) {
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || !parsed.formConfig) {
            return null;
        }
        return parsed;
    }
    catch {
        return null;
    }
}
export const ConfigTransferSection = ({ configRecord, onConfigRecordChange, }) => {
    const [expanded, setExpanded] = useState(false);
    const [copied, setCopied] = useState(false);
    const [pasteText, setPasteText] = useState('');
    const [parseError, setParseError] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingPayload, setPendingPayload] = useState(null);
    const diff = useMemo(() => {
        if (!pendingPayload)
            return null;
        return computeFieldDiff(configRecord.formConfig?.fields || [], pendingPayload.formConfig?.fields || [], configRecord.styleConfig, pendingPayload.styleConfig, configRecord.actionsConfig, pendingPayload.actionsConfig);
    }, [pendingPayload, configRecord]);
    const handleCopy = async () => {
        const { key: _key, ...formConfigWithoutKey } = configRecord.formConfig;
        const payload = {
            formConfig: formConfigWithoutKey,
            styleConfig: configRecord.styleConfig,
            actionsConfig: configRecord.actionsConfig,
        };
        await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    const handleImport = () => {
        setParseError('');
        const parsed = parseTransferPayload(pasteText.trim());
        if (!parsed) {
            setParseError('Invalid config JSON. Must be an object with at least a "formConfig" property.');
            return;
        }
        setPendingPayload(parsed);
        setShowConfirmModal(true);
    };
    const handleConfirm = () => {
        if (!pendingPayload)
            return;
        const sanitized = {
            formConfig: {
                ...pendingPayload.formConfig,
                key: configRecord.formKey,
            },
        };
        if (pendingPayload.styleConfig) {
            sanitized.styleConfig = pendingPayload.styleConfig;
        }
        if (pendingPayload.actionsConfig) {
            sanitized.actionsConfig = pendingPayload.actionsConfig;
        }
        onConfigRecordChange(sanitized);
        setShowConfirmModal(false);
        setPendingPayload(null);
        setPasteText('');
    };
    const handleCancel = () => {
        setShowConfirmModal(false);
        setPendingPayload(null);
    };
    const totalChanges = diff
        ? diff.added.length + diff.removed.length + diff.modified.length + (diff.styleChanged ? 1 : 0) + (diff.actionsChanged ? 1 : 0)
        : 0;
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "mt-6 border border-gray-200 rounded-xl shadow-sm" },
            React.createElement("button", { onClick: () => setExpanded(!expanded), className: "w-full flex justify-between items-center p-3 bg-gradient-to-r from-gray-50 to-white rounded-t-xl hover:from-gray-100 transition-colors" },
                React.createElement("h3", { className: "font-medium text-gray-700 text-sm flex items-center gap-2" },
                    React.createElement(ClipboardPaste, { className: "w-4 h-4" }),
                    "Import / Export Config"),
                expanded ? React.createElement(ChevronUp, { className: "w-4 h-4 text-gray-500" }) : React.createElement(ChevronDown, { className: "w-4 h-4 text-gray-500" })),
            expanded && (React.createElement("div", { className: "p-4 space-y-4 border-t border-gray-100" },
                React.createElement("div", null,
                    React.createElement("p", { className: "text-xs text-gray-500 mb-2" }, "Copy the current form configuration (fields, styles, and actions) to your clipboard."),
                    React.createElement(Button, { size: "small", basic: true, color: copied ? 'green' : undefined, onClick: handleCopy },
                        copied ? React.createElement(Check, { className: "w-3.5 h-3.5 mr-1.5 inline" }) : React.createElement(Copy, { className: "w-3.5 h-3.5 mr-1.5 inline" }),
                        copied ? 'Copied!' : 'Copy Config')),
                React.createElement("div", { className: "border-t border-gray-100" }),
                React.createElement("div", null,
                    React.createElement("p", { className: "text-xs text-gray-500 mb-2" }, "Paste a config JSON below to import it into this form."),
                    React.createElement("textarea", { className: "w-full h-28 p-2 border border-gray-200 rounded-lg text-xs font-mono resize-y focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300", placeholder: 'Paste config JSON here...', value: pasteText, onChange: (e) => {
                            setPasteText(e.target.value);
                            setParseError('');
                        } }),
                    parseError && (React.createElement("p", { className: "text-xs text-red-600 mt-1 flex items-center gap-1" },
                        React.createElement(AlertTriangle, { className: "w-3 h-3" }),
                        parseError)),
                    React.createElement("div", { className: "mt-2" },
                        React.createElement(Button, { size: "small", primary: true, disabled: !pasteText.trim(), onClick: handleImport },
                            React.createElement(ClipboardPaste, { className: "w-3.5 h-3.5 mr-1.5 inline" }),
                            "Import Config")))))),
        React.createElement(Modal, { open: showConfirmModal, onClose: handleCancel, size: "small" },
            React.createElement(Modal.Header, null, "Confirm Config Import"),
            React.createElement(Modal.Content, null, diff && totalChanges === 0 ? (React.createElement("p", { className: "text-gray-600" }, "No differences detected. The pasted config matches the current one.")) : diff && (React.createElement("div", { className: "space-y-3" },
                React.createElement("p", { className: "text-sm text-gray-600 mb-3" }, "The following changes will be applied to your form:"),
                diff.added.length > 0 && (React.createElement("div", { className: "border border-green-200 rounded-lg p-3 bg-green-50" },
                    React.createElement("h4", { className: "text-sm font-medium text-green-800 flex items-center gap-1.5 mb-2" },
                        React.createElement(Plus, { className: "w-4 h-4" }),
                        diff.added.length,
                        " field",
                        diff.added.length > 1 ? 's' : '',
                        " added"),
                    React.createElement("ul", { className: "text-xs text-green-700 space-y-1" }, diff.added.map(f => (React.createElement("li", { key: f.key, className: "flex items-center gap-1" },
                        React.createElement("span", { className: "font-mono bg-green-100 px-1 rounded" }, f.key),
                        React.createElement("span", { className: "text-green-600" },
                            "(",
                            f.type,
                            ")"),
                        f.label && React.createElement("span", null,
                            "\u2014 ",
                            f.label))))))),
                diff.removed.length > 0 && (React.createElement("div", { className: "border border-red-200 rounded-lg p-3 bg-red-50" },
                    React.createElement("h4", { className: "text-sm font-medium text-red-800 flex items-center gap-1.5 mb-2" },
                        React.createElement(Minus, { className: "w-4 h-4" }),
                        diff.removed.length,
                        " field",
                        diff.removed.length > 1 ? 's' : '',
                        " removed"),
                    React.createElement("ul", { className: "text-xs text-red-700 space-y-1" }, diff.removed.map(f => (React.createElement("li", { key: f.key, className: "flex items-center gap-1" },
                        React.createElement("span", { className: "font-mono bg-red-100 px-1 rounded" }, f.key),
                        React.createElement("span", { className: "text-red-600" },
                            "(",
                            f.type,
                            ")"),
                        f.label && React.createElement("span", null,
                            "\u2014 ",
                            f.label))))))),
                diff.modified.length > 0 && (React.createElement("div", { className: "border border-amber-200 rounded-lg p-3 bg-amber-50" },
                    React.createElement("h4", { className: "text-sm font-medium text-amber-800 flex items-center gap-1.5 mb-2" },
                        React.createElement(RefreshCw, { className: "w-4 h-4" }),
                        diff.modified.length,
                        " field",
                        diff.modified.length > 1 ? 's' : '',
                        " modified"),
                    React.createElement("ul", { className: "text-xs text-amber-700 space-y-1" }, diff.modified.map(({ current, incoming }) => (React.createElement("li", { key: incoming.key, className: "flex items-center gap-1 flex-wrap" },
                        React.createElement("span", { className: "font-mono bg-amber-100 px-1 rounded" }, incoming.key),
                        current.type !== incoming.type && (React.createElement("span", null,
                            "type: ",
                            React.createElement("span", { className: "line-through" }, current.type),
                            " \u2192 ",
                            incoming.type)),
                        current.label !== incoming.label && (React.createElement("span", null,
                            "label: ",
                            React.createElement("span", { className: "line-through" }, current.label),
                            " \u2192 ",
                            incoming.label)))))))),
                diff.styleChanged && (React.createElement("div", { className: "border border-blue-200 rounded-lg p-2 bg-blue-50" },
                    React.createElement("p", { className: "text-xs text-blue-700 font-medium flex items-center gap-1.5" },
                        React.createElement(RefreshCw, { className: "w-3.5 h-3.5" }),
                        "Style configuration will be updated"))),
                diff.actionsChanged && (React.createElement("div", { className: "border border-purple-200 rounded-lg p-2 bg-purple-50" },
                    React.createElement("p", { className: "text-xs text-purple-700 font-medium flex items-center gap-1.5" },
                        React.createElement(RefreshCw, { className: "w-3.5 h-3.5" }),
                        "Actions configuration will be updated")))))),
            React.createElement(Modal.Actions, null,
                React.createElement(Button, { basic: true, onClick: handleCancel }, "Cancel"),
                React.createElement(Button, { primary: true, onClick: handleConfirm }, "Apply Changes")))));
};
